import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Search, X, Check, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Reorder, useDragControls } from 'framer-motion';
import TaskColumn from '../components/TaskBoard/TaskColumn';
import NewTaskModal from '../components/TaskBoard/NewTaskModal';
import BoardSwitcher from '../components/TaskBoard/BoardSwitcher';
import AddColumnButton from '../components/TaskBoard/AddColumnButton';
import MentionInput from '../components/TaskBoard/MentionInput';
import CommentThread from '../components/TaskBoard/CommentThread';

// Reorderable wrapper for one column. Uses pointer-driven drag started ONLY
// from the column header. Task cards inside have their own framer-motion drag.
const ReorderableColumn = ({ col, onPointerUpEnd, ...rest }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={col}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onPointerUpEnd}
      className="flex-shrink-0"
      whileDrag={{ scale: 1.03, zIndex: 50, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
    >
      <TaskColumn
        column={col}
        onColumnDragHandlePointerDown={(e) => controls.start(e)}
        {...rest}
      />
    </Reorder.Item>
  );
};

// Resolve API base once. REACT_APP_API_URL can be either the bare origin
// (https://api.glowytics.com) or include a trailing /api — normalise both.
const API = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const BOARD_KEY = (brandId) => `taskboard:selectedBoardId:${brandId}`;

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const getAuthHeaders = () => {
  const token =
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('accessToken') ||
    document.cookie.replace(/(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/, '$1');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiFetch = async (url, options = {}) => {
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

// Simple save-indicator states
const SAVE_IDLE = 'idle';
const SAVE_SAVING = 'saving';
const SAVE_DONE = 'done';
const SAVE_ERR = 'error';

// ---------------------------------------------------------------------------
// TaskBoard
// ---------------------------------------------------------------------------
const TaskBoard = ({ brandId }) => {
  const currentBrandId = localStorage.getItem('currentBrandId') || brandId;

  // ---- boards ----
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [brandUsers, setBrandUsers] = useState([]);

  // ---- columns ----
  const [columns, setColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);

  // ---- tasks ----
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  // ---- UI ----
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [newTaskColumnId, setNewTaskColumnId] = useState(null);

  // Debounce search input (300ms) so each keystroke doesn't refetch.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ---- detail modal ----
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editedTask, setEditedTask] = useState(null);
  const [saveStatus, setSaveStatus] = useState(SAVE_IDLE);
  const [newComment, setNewComment] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ---- column drag ----

  // ---- delete board confirm ----
  const [deleteBoardTarget, setDeleteBoardTarget] = useState(null);

  // debounce timer ref
  const saveTimerRef = useRef(null);
  // mirror columns to a ref so async handlers (e.g. drag end) read freshest order
  const columnsRef = useRef(columns);
  useEffect(() => { columnsRef.current = columns; }, [columns]);

  // ======================================================================
  // Data fetching
  // ======================================================================

  const fetchBoards = useCallback(async () => {
    if (!currentBrandId) return;
    setBoardsLoading(true);
    try {
      const data = await apiFetch(
        `${API}/api/tasks/boards?brandId=${currentBrandId}&include_archived=true`,
        { headers: { 'X-Brand-ID': currentBrandId } }
      );
      const boardList = data.boards || [];
      setBoards(boardList);

      // restore or fall back to default
      const stored = localStorage.getItem(BOARD_KEY(currentBrandId));
      const storedBoard = stored && boardList.find((b) => b.id === stored && !b.is_archived);
      const defaultBoard = boardList.find((b) => b.is_default) || boardList[0];
      setSelectedBoardId((storedBoard || defaultBoard)?.id || null);
    } catch (err) {
      toast.error('Could not load boards');
    } finally {
      setBoardsLoading(false);
    }
  }, [currentBrandId]);

  const fetchColumns = useCallback(async (boardId) => {
    if (!boardId || !currentBrandId) return;
    setColumnsLoading(true);
    try {
      const data = await apiFetch(
        `${API}/api/tasks/boards/${boardId}/columns?brandId=${currentBrandId}`,
        { headers: { 'X-Brand-ID': currentBrandId } }
      );
      setColumns((data.columns || []).sort((a, b) => a.position - b.position));
    } catch (err) {
      toast.error('Could not load columns');
    } finally {
      setColumnsLoading(false);
    }
  }, [currentBrandId]);

  const fetchTasks = useCallback(async (boardId) => {
    if (!boardId || !currentBrandId) return;
    setTasksLoading(true);
    try {
      const params = new URLSearchParams({
        boardId,
        brandId: currentBrandId,
        ...(debouncedSearch ? { searchQuery: debouncedSearch } : {}),
        ...(filterType === 'mine'
          ? { mine: 'true' }
          : filterType !== 'all' ? { type: filterType } : {}),
      });
      const data = await apiFetch(
        `${API}/api/tasks?${params}`,
        { headers: { 'X-Brand-ID': currentBrandId } }
      );
      setTasks(data.tasks || []);
    } catch (err) {
      toast.error('Could not load tasks');
    } finally {
      setTasksLoading(false);
    }
  }, [currentBrandId, debouncedSearch, filterType]);

  const fetchBrandUsers = useCallback(async () => {
    if (!currentBrandId) return;
    try {
      const data = await apiFetch(
        `${API}/api/tasks/brand-users?brandId=${currentBrandId}`,
        { headers: { 'X-Brand-ID': currentBrandId } }
      );
      setBrandUsers(data.users || []);
    } catch (err) {
      console.warn('Could not load brand users:', err.message);
    }
  }, [currentBrandId]);

  useEffect(() => { fetchBoards(); fetchBrandUsers(); }, [fetchBoards, fetchBrandUsers]);

  // Sağ alttaki Task kulakçığından açılan görev bu panodaysa listeyi tazele.
  useEffect(() => {
    const onQuickTask = () => { if (selectedBoardId) fetchTasks(selectedBoardId); };
    window.addEventListener('quick-task-created', onQuickTask);
    return () => window.removeEventListener('quick-task-created', onQuickTask);
  }, [selectedBoardId, fetchTasks]);

  useEffect(() => {
    if (!selectedBoardId) return;
    localStorage.setItem(BOARD_KEY(currentBrandId), selectedBoardId);
    fetchColumns(selectedBoardId);
    fetchTasks(selectedBoardId);
  }, [selectedBoardId, fetchColumns, fetchTasks, currentBrandId]);

  // re-fetch tasks when filter/debounced-search changes
  useEffect(() => {
    if (selectedBoardId) fetchTasks(selectedBoardId);
  }, [debouncedSearch, filterType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ======================================================================
  // Board actions
  // ======================================================================

  const handleSelectBoard = (boardId) => {
    setSelectedBoardId(boardId);
    setTasks([]);
    setColumns([]);
  };

  const handleCreateBoard = async (name) => {
    try {
      const data = await apiFetch(`${API}/api/tasks/boards`, {
        method: 'POST',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ name, brandId: currentBrandId }),
      });
      const newBoard = data.board;
      setBoards((prev) => [...prev, newBoard]);
      setSelectedBoardId(newBoard.id);
      toast.success(`Board "${name}" created`);
    } catch {
      toast.error('Could not create board');
    }
  };

  const handleRenameBoard = async (boardId, newName) => {
    try {
      const data = await apiFetch(`${API}/api/tasks/boards/${boardId}`, {
        method: 'PUT',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ name: newName, brandId: currentBrandId }),
      });
      setBoards((prev) => prev.map((b) => (b.id === boardId ? data.board : b)));
    } catch {
      toast.error('Could not rename board');
    }
  };

  const handleArchiveBoard = async (boardId) => {
    try {
      await apiFetch(`${API}/api/tasks/boards/${boardId}/archive`, {
        method: 'POST',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ brandId: currentBrandId }),
      });
      setBoards((prev) =>
        prev.map((b) => (b.id === boardId ? { ...b, is_archived: true } : b))
      );
      if (selectedBoardId === boardId) {
        const fallback = boards.find((b) => !b.is_archived && b.id !== boardId);
        setSelectedBoardId(fallback?.id || null);
      }
      toast.success('Board archived');
    } catch {
      toast.error('Could not archive board');
    }
  };

  const handleUnarchiveBoard = async (boardId) => {
    try {
      await apiFetch(`${API}/api/tasks/boards/${boardId}/unarchive`, {
        method: 'POST',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ brandId: currentBrandId }),
      });
      setBoards((prev) =>
        prev.map((b) => (b.id === boardId ? { ...b, is_archived: false } : b))
      );
      toast.success('Board unarchived');
    } catch {
      toast.error('Could not unarchive board');
    }
  };

  const handleDeleteBoard = (boardId) => {
    setDeleteBoardTarget(boardId);
  };

  const handleConfirmDeleteBoard = async () => {
    const boardId = deleteBoardTarget;
    setDeleteBoardTarget(null);
    try {
      await apiFetch(`${API}/api/tasks/boards/${boardId}`, {
        method: 'DELETE',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ brandId: currentBrandId }),
      });
      const remaining = boards.filter((b) => b.id !== boardId);
      setBoards(remaining);
      if (selectedBoardId === boardId) {
        const fallback = remaining.find((b) => !b.is_archived) || remaining[0];
        setSelectedBoardId(fallback?.id || null);
      }
      toast.success('Board deleted');
    } catch {
      toast.error('Could not delete board');
    }
  };

  // ======================================================================
  // Column actions
  // ======================================================================

  const handleAddColumn = async (name) => {
    try {
      const data = await apiFetch(`${API}/api/tasks/boards/${selectedBoardId}/columns`, {
        method: 'POST',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ name, brandId: currentBrandId }),
      });
      setColumns((prev) => [...prev, data.column].sort((a, b) => a.position - b.position));
      toast.success(`Column "${name}" added`);
    } catch {
      toast.error('Could not add column');
    }
  };

  const handleRenameColumn = async (columnId, name) => {
    try {
      const data = await apiFetch(`${API}/api/tasks/columns/${columnId}`, {
        method: 'PUT',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ name, brandId: currentBrandId }),
      });
      setColumns((prev) =>
        prev.map((c) => (c.id === columnId ? { ...c, name: data.column?.name || name } : c))
      );
    } catch {
      toast.error('Could not rename column');
    }
  };

  const handleChangeColumnColor = async (columnId, color) => {
    // optimistic
    setColumns((prev) => prev.map((c) => (c.id === columnId ? { ...c, color } : c)));
    try {
      await apiFetch(`${API}/api/tasks/columns/${columnId}`, {
        method: 'PUT',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ color, brandId: currentBrandId }),
      });
    } catch {
      toast.error('Could not update column color');
      fetchColumns(selectedBoardId); // rollback
    }
  };

  const handleDeleteColumn = async (columnId) => {
    try {
      await apiFetch(`${API}/api/tasks/columns/${columnId}`, {
        method: 'DELETE',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ brandId: currentBrandId }),
      });
      setColumns((prev) => prev.filter((c) => c.id !== columnId));
      await fetchTasks(selectedBoardId); // tasks may have moved
      toast.success('Column deleted');
    } catch {
      toast.error('Could not delete column');
    }
  };

  // ---- column reorder via framer-motion (pointer-driven, smooth) ----
  // newOtherCols = the new ordering of non-AI columns (AI col is fixed at position 0)
  const handleColumnReorder = (newOtherCols) => {
    const aiCol = columns.find((c) => c.is_ai_suggestions);
    const combined = aiCol ? [aiCol, ...newOtherCols] : newOtherCols;
    const withPositions = combined.map((c, i) => ({ ...c, position: i }));
    setColumns(withPositions);
  };

  const persistColumnOrder = useCallback(async () => {
    const ordered = (columnsRef.current || []).filter((c) => !c.is_ai_suggestions);
    const payload = {
      boardId: selectedBoardId,
      brandId: currentBrandId,
      columnIds: ordered.map((c) => c.id),
    };
    console.log(
      '[TaskBoard] persisting column order — boardId:', payload.boardId,
      '| brandId:', payload.brandId,
      '| columnIds:', payload.columnIds,
      '| ordered cols:', ordered.map((c) => ({ id: c.id, name: c.name }))
    );
    if (!payload.boardId || !payload.brandId || payload.columnIds.length === 0) {
      console.warn('[TaskBoard] skipping persist: missing field', payload);
      return;
    }
    try {
      const data = await apiFetch(`${API}/api/tasks/columns/reorder`, {
        method: 'POST',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify(payload),
      });
      console.log('[TaskBoard] reorder response:', data);
    } catch (err) {
      console.error('[TaskBoard] reorder failed:', err);
      toast.error('Could not save column order');
      fetchColumns(selectedBoardId);
    }
  }, [currentBrandId, selectedBoardId, fetchColumns]);

  // ======================================================================
  // Task actions
  // ======================================================================

  const getTasksForColumn = (columnId) =>
    tasks.filter((t) => t.column_id === columnId);

  // Move a task between columns (called by motion drag-end hit-test in TaskCard)
  const handleMoveTaskToColumn = async (taskId, targetColumnId) => {
    const taskToUpdate = tasks.find((t) => t.id === taskId);
    if (!taskToUpdate || taskToUpdate.column_id === targetColumnId) return;

    const originalTasks = [...tasks];
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, column_id: targetColumnId } : t))
    );

    try {
      const data = await apiFetch(`${API}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ columnId: targetColumnId, brandId: currentBrandId }),
      });
      if (data.task) {
        setTasks((prev) => prev.map((t) => (t.id === taskId ? data.task : t)));
      }
    } catch {
      toast.error('Could not move task');
      setTasks(originalTasks);
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setEditedTask({ ...task });
    setSaveStatus(SAVE_IDLE);
    setNewComment('');
    setShowDeleteConfirm(false);
    setIsDetailModalOpen(true);
  };

  const handleApplyTask = async (taskId) => {
    const taskToUpdate = tasks.find((t) => t.id === taskId);
    if (!taskToUpdate) return;

    const originalTasks = [...tasks];
    const updatedData = { ...taskToUpdate, status: 'completed', type: 'user' };
    setTasks((prev) => prev.map((t) => (t.id === taskId ? updatedData : t)));
    if (selectedTask?.id === taskId) setSelectedTask(updatedData);

    try {
      const response = await apiFetch(`${API}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ status: 'completed', type: 'user', brandId: currentBrandId }),
      });
      if (response.task) {
        setTasks((prev) => prev.map((t) => (t.id === taskId ? response.task : t)));
        if (selectedTask?.id === taskId) setSelectedTask(response.task);
      }
    } catch {
      toast.error('Could not apply suggestion');
      setTasks(originalTasks);
    }
  };

  const handleNewTask = (columnId) => {
    setNewTaskColumnId(columnId);
    setIsNewTaskModalOpen(true);
  };

  const handleCreateTask = async (newTaskData) => {
    if (!currentBrandId) { toast.error('Brand ID missing'); return; }
    const payload = {
      ...newTaskData,
      brandId: currentBrandId,
      boardId: selectedBoardId,
      columnId: newTaskData.column_id || newTaskColumnId || null,
      type: newTaskData.type || 'user',
      status: newTaskData.status || 'todo',
    };
    console.log('[TaskBoard] creating task:', payload);
    try {
      const data = await apiFetch(`${API}/api/tasks`, {
        method: 'POST',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify(payload),
      });
      if (data.task) {
        setTasks((prev) => [data.task, ...prev]);
        setIsNewTaskModalOpen(false);
        toast.success('Task created');
      }
    } catch (err) {
      console.error('[TaskBoard] create task failed:', err);
      toast.error('Could not create task');
    }
  };

  // ---- detail modal save (on blur, debounced 500ms) ----
  const persistTaskField = useCallback(async (taskId, field, value) => {
    setSaveStatus(SAVE_SAVING);
    // Map frontend snake_case field names to the backend's camelCase API
    const apiField =
      field === 'column_id' ? 'columnId'
      : field === 'board_id' ? 'boardId'
      : field;
    try {
      const data = await apiFetch(`${API}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ [apiField]: value, brandId: currentBrandId }),
      });
      if (data.task) {
        setTasks((prev) => prev.map((t) => (t.id === taskId ? data.task : t)));
        setSelectedTask(data.task);
        setEditedTask((prev) => ({ ...prev, ...data.task }));
      }
      setSaveStatus(SAVE_DONE);
      setTimeout(() => setSaveStatus(SAVE_IDLE), 2000);
    } catch {
      setSaveStatus(SAVE_ERR);
      toast.error('Could not save task');
    }
  }, [currentBrandId]);

  const handleDetailFieldChange = (field, value) => {
    setEditedTask((prev) => ({ ...prev, [field]: value }));
    setSaveStatus(SAVE_SAVING);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      persistTaskField(editedTask.id, field, value);
    }, 500);
  };

  const handleDetailFieldBlur = (field, value) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (editedTask && editedTask[field] !== selectedTask[field]) {
      persistTaskField(editedTask.id, field, value);
    }
  };

  const handleCloseModal = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      // flush pending save
      if (editedTask && selectedTask && saveStatus === SAVE_SAVING) {
        persistTaskField(editedTask.id, '_flush_', null);
      }
    }
    setIsDetailModalOpen(false);
    setSelectedTask(null);
    setEditedTask(null);
    setSaveStatus(SAVE_IDLE);
    setNewComment('');
    setShowDeleteConfirm(false);
  };

  const handleAddComment = async (text, parentCommentId = null) => {
    const body = (typeof text === 'string' ? text : newComment).trim();
    if (!body || !selectedTask) return;
    try {
      const data = await apiFetch(`${API}/api/tasks/${selectedTask.id}/comments`, {
        method: 'POST',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({
          comment: body,
          brandId: currentBrandId,
          parent_comment_id: parentCommentId,
        }),
      });
      const added = data.comment;
      setSelectedTask((prev) => ({ ...prev, comments: [...(prev.comments || []), added] }));
      setEditedTask((prev) => ({ ...prev, comments: [...(prev.comments || []), added] }));
      setTasks((prev) =>
        prev.map((t) =>
          t.id === selectedTask.id
            ? { ...t, comments: [...(t.comments || []), added] }
            : t
        )
      );
      if (!parentCommentId) setNewComment('');
    } catch {
      toast.error('Could not add comment');
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    try {
      await apiFetch(`${API}/api/tasks/${selectedTask.id}`, {
        method: 'DELETE',
        headers: { 'X-Brand-ID': currentBrandId },
        body: JSON.stringify({ brandId: currentBrandId }),
      });
      setTasks((prev) => prev.filter((t) => t.id !== selectedTask.id));
      setShowDeleteConfirm(false);
      handleCloseModal();
      toast.success('Task deleted');
    } catch {
      toast.error('Could not delete task');
      setShowDeleteConfirm(false);
    }
  };

  // ======================================================================
  // Derived values
  // ======================================================================
  const firstNonAiColumn = columns.find((c) => !c.is_ai_suggestions);

  const SaveIndicator = () => {
    if (saveStatus === SAVE_IDLE) return null;
    if (saveStatus === SAVE_SAVING)
      return (
        <span className="flex items-center gap-1 text-xs text-gray-400">
          <Loader2 size={12} className="animate-spin" /> Saving…
        </span>
      );
    if (saveStatus === SAVE_DONE)
      return (
        <span className="flex items-center gap-1 text-xs text-green-600">
          <Check size={12} /> Saved
        </span>
      );
    if (saveStatus === SAVE_ERR)
      return <span className="text-xs text-red-500">Save failed</span>;
    return null;
  };

  const initialLoading = boardsLoading && boards.length === 0;

  // ======================================================================
  // Render
  // ======================================================================
  return (
    <div className="flex flex-col h-screen bg-[#f6f8ff]/70">
      {/* ---- header bar ---- */}
      <div className="flex justify-between items-center px-4 pt-4 pb-2">
        <h1 className="text-lg font-semibold text-gray-800">Task Board</h1>

        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm bg-white border border-gray-200 text-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
            />
            <Search className="absolute left-2.5 top-2 text-gray-400" size={16} />
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-2 py-1.5 text-sm bg-white border border-gray-200 text-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All Tasks</option>
            <option value="mine">My Tasks</option>
            <option value="user">User Tasks</option>
            <option value="suggestion">Suggestions</option>
          </select>

          <button
            onClick={() => handleNewTask(firstNonAiColumn?.id || null)}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
          >
            <Plus size={16} />
            New Task
          </button>
        </div>
      </div>

      {/* ---- board switcher ---- */}
      {!initialLoading && (
        <BoardSwitcher
          boards={boards}
          selectedBoardId={selectedBoardId}
          showArchived={showArchived}
          onSelectBoard={handleSelectBoard}
          onCreateBoard={handleCreateBoard}
          onRenameBoard={handleRenameBoard}
          onArchiveBoard={handleArchiveBoard}
          onUnarchiveBoard={handleUnarchiveBoard}
          onDeleteBoard={handleDeleteBoard}
          onToggleArchived={() => setShowArchived((v) => !v)}
        />
      )}

      {/* ---- full-page spinner only on very first load ---- */}
      {initialLoading && (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-blue-500" size={32} />
        </div>
      )}

      {/* ---- columns area ---- */}
      {!initialLoading && (
        <div className="flex-1 flex gap-4 px-4 pb-4 pt-3 overflow-x-auto">
          {columnsLoading ? (
            // skeleton placeholders
            [1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-[280px] flex-shrink-0 bg-white rounded-xl shadow-sm animate-pulse"
                style={{ height: 'calc(100% - 16px)' }}
              >
                <div className="h-10 bg-gray-200 rounded-t-xl" />
                <div className="p-3 space-y-2">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="h-20 bg-gray-100 rounded-xl" />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <>
              {/* AI Suggestions column — fixed leftmost, NOT reorderable */}
              {columns.filter((c) => c.is_ai_suggestions).map((col) => (
                <div key={col.id} className="flex-shrink-0">
                  <TaskColumn
                    column={col}
                    tasks={tasksLoading ? [] : getTasksForColumn(col.id)}
                    firstNonAiColumnName={firstNonAiColumn?.name || 'To-Do'}
                    onAddTask={() => handleNewTask(col.id)}
                    onTaskClick={handleTaskClick}
                    onApplyTask={handleApplyTask}
                    onMoveTaskToColumn={handleMoveTaskToColumn}
                    onRenameColumn={handleRenameColumn}
                    onChangeColumnColor={handleChangeColumnColor}
                    onDeleteColumn={handleDeleteColumn}
                  />
                </div>
              ))}

              {/* Reorderable non-AI columns */}
              <Reorder.Group
                axis="x"
                values={columns.filter((c) => !c.is_ai_suggestions)}
                onReorder={handleColumnReorder}
                className="flex gap-4 list-none m-0 p-0"
                as="div"
              >
                {columns.filter((c) => !c.is_ai_suggestions).map((col) => (
                  <ReorderableColumn
                    key={col.id}
                    col={col}
                    onPointerUpEnd={persistColumnOrder}
                    tasks={tasksLoading ? [] : getTasksForColumn(col.id)}
                    firstNonAiColumnName={firstNonAiColumn?.name || 'To-Do'}
                    onAddTask={() => handleNewTask(col.id)}
                    onTaskClick={handleTaskClick}
                    onApplyTask={handleApplyTask}
                    onMoveTaskToColumn={handleMoveTaskToColumn}
                    onRenameColumn={handleRenameColumn}
                    onChangeColumnColor={handleChangeColumnColor}
                    onDeleteColumn={handleDeleteColumn}
                  />
                ))}
              </Reorder.Group>

              <AddColumnButton onAddColumn={handleAddColumn} />
            </>
          )}
        </div>
      )}

      {/* ---- new task modal ---- */}
      <NewTaskModal
        isOpen={isNewTaskModalOpen}
        onClose={() => setIsNewTaskModalOpen(false)}
        onSave={handleCreateTask}
        columnId={newTaskColumnId}
        brandUsers={brandUsers}
      />

      {/* ---- task detail modal (editable, save on blur / debounce) ---- */}
      {isDetailModalOpen && editedTask && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Task Details</h2>
              <div className="flex items-center gap-3">
                <SaveIndicator />
                <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {/* title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <MentionInput
                  as="input"
                  type="text"
                  value={editedTask.title || ''}
                  onChange={(v) => handleDetailFieldChange('title', v)}
                  onBlur={(e) => handleDetailFieldBlur('title', e.target.value)}
                  brandUsers={brandUsers}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Type @ to mention someone"
                />
              </div>

              {/* description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <MentionInput
                  as="textarea"
                  value={editedTask.description || ''}
                  onChange={(v) => handleDetailFieldChange('description', v)}
                  onBlur={(e) => handleDetailFieldBlur('description', e.target.value)}
                  brandUsers={brandUsers}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Type @ to mention someone"
                />
                {/* Task kulakçığından eklenen ekran görüntüleri */}
                {(editedTask.description || '').match(/https?:\/\/\S+\/api\/tasks\/shots\/[a-f0-9]{48}\.jpg/g)?.map((src) => (
                  <a key={src} href={src} target="_blank" rel="noreferrer" className="block mt-2">
                    <img src={src} alt="Screenshot" className="w-full rounded-lg border border-gray-200" />
                  </a>
                ))}
              </div>

              {/* priority + due date */}
              <div className="flex gap-4">
                <div className="w-1/2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={editedTask.priority || 3}
                    onChange={(e) => handleDetailFieldChange('priority', parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={1}>High</option>
                    <option value={2}>Medium</option>
                    <option value={3}>Low</option>
                  </select>
                </div>

                <div className="w-1/2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={editedTask.due_date ? editedTask.due_date.slice(0, 10) : ''}
                    onChange={(e) => handleDetailFieldChange('due_date', e.target.value || null)}
                    onBlur={(e) => handleDetailFieldBlur('due_date', e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* column selector */}
              {columns.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Column</label>
                  <select
                    value={editedTask.column_id || ''}
                    onChange={(e) => handleDetailFieldChange('column_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {columns.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* assignee selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
                <select
                  value={editedTask.assigned_to || ''}
                  onChange={(e) => {
                    const v = e.target.value || null;
                    handleDetailFieldChange('assigned_to', v);
                    handleDetailFieldBlur('assigned_to', v);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">— Unassigned —</option>
                  {brandUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email} {u.role === 'OWNER' ? '(Owner)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* apply suggestion button */}
              {editedTask.type === 'ai' && editedTask.status !== 'completed' && (
                <button
                  onClick={() => handleApplyTask(editedTask.id)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                >
                  Apply Suggestion
                </button>
              )}

              {/* delete */}
              <div>
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Task
                  </button>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-3">
                      Delete this task? All comments will also be removed. This cannot be undone.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteTask}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* comments */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-lg font-medium mb-3">Comments</h3>
                <div className="mb-4 max-h-72 overflow-y-auto pr-1">
                  <CommentThread
                    comments={editedTask.comments || []}
                    brandUsers={brandUsers}
                    onReply={(text, parentId) => handleAddComment(text, parentId)}
                  />
                </div>

                <div className="flex gap-2">
                  <MentionInput
                    as="textarea"
                    value={newComment}
                    onChange={setNewComment}
                    brandUsers={brandUsers}
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                    placeholder="Add a comment… (@ to mention, Enter to send)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                  <button
                    onClick={() => handleAddComment()}
                    disabled={!newComment.trim()}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed self-start"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- delete board confirmation modal ---- */}
      {deleteBoardTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-2">Delete Board</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will permanently delete the board and all its tasks and columns. This cannot be
              undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeleteBoardTarget(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteBoard}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1"
              >
                <Trash2 size={15} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskBoard;
